/** EMBER & PALM — high-value events sales page based on the owner-approved October 2026 package. */
import { FormEvent, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Download,
  GlassWater,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  Users,
} from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import {
  DRINK_PACKAGES,
  EVENT_TYPES,
  VENUE_MINIMUMS,
} from "@/lib/events";
import { SITE, trackEvent } from "@/lib/site";

const FEAST_MENU_REV25 = {
  image: "/manus-storage/angkor-feast-menus-FINAL-rev25-web_49e505c1.png",
  pdf: "/manus-storage/angkor-feast-menus-FINAL-rev25_0edbed4f.pdf",
} as const;

const kitchenUpgrades = [
  ["Welcome canapés · chef’s selection, 2 pieces", "$10 pp"],
  ["Extra entrée · prawn wontons, BBQ scallop* or beef skewer", "$7 pp"],
  ["Extra main · Kampot Pepper Seafood or BBQ Steak", "$10 pp"],
  ["Angkor Grilled Chicken Share Plate", "$55.90 each"],
  ["Dessert Trio · upgrade on Angkor Feast", "+$6 pp"],
  ["Kids menu · main, ice cream and juice", "$29"],
  ["Halal-sourced meat · on request", "+$5 pp"],
] as const;

const styleUpgrades = [
  ["Welcome sparkling or Angkor Tropical cocktail", "$14 pp"],
  ["Welcome mocktail · Angkor Lemon Sunset Soda", "$9 pp"],
  ["Celebration cake · stored, cut and served", "$3 pp"],
  ["Celebration cake with coconut ice cream", "$7 pp"],
  ["White linen · tablecloths and napkins", "from $250"],
  ["Black & gold styling · candles and fresh flowers", "from $375"],
  ["Personalised printed menus", "$3 pp"],
] as const;

function DownloadPackage({ placement }: { placement: string }) {
  return (
    <a
      className="button button-outline"
      href={SITE.eventsPackage}
      download="ANGKOR-Events-and-Functions-Packages.pdf"
      onClick={() =>
        trackEvent("event_package_download", {
          placement,
        })
      }
    >
      <Download size={17} /> Download Event Package
    </a>
  );
}

export default function GroupsFunctionsPage() {
  const [copyMessage, setCopyMessage] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const minimumDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Melbourne",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const lines = [
      "ANGKOR event enquiry",
      "",
      ...Array.from(form.entries()).map(([key, value]) => `${key}: ${value}`),
    ];
    const message = lines.join("\n");
    setCopyMessage(message);
    setCopyStatus("");
    trackEvent("event_enquiry", { method: "email_form" });
    window.location.href = `mailto:${SITE.eventsEmail}?subject=${encodeURIComponent("ANGKOR event & function enquiry")}&body=${encodeURIComponent(message)}`;
  }

  async function copyEnquiry() {
    if (!copyMessage) return;
    try {
      await navigator.clipboard.writeText(copyMessage);
      setCopyStatus(
        "Enquiry details copied. Paste them into an email to ANGKOR."
      );
    } catch {
      setCopyStatus(
        `Copy was unavailable. Please email ${SITE.eventsEmail} or call ${SITE.phoneDisplay}.`
      );
    }
  }

  return (
    <>
      <PageMeta
        title="Functions & Groups | ANGKOR Cambodian Flavours"
        description="Cambodian feast packages for birthdays, weddings and corporate functions. Private venue hire for up to 38 seated guests in West Melbourne."
        path="/groups-functions"
        image={SITE.images.khmerGrill}
      />

      <section className="page-hero groups-hero function-sales-hero">
        <img
          src={SITE.images.khmerGrill}
          alt="Cambodian sharing feast prepared for an ANGKOR event table"
          width="1600"
          height="1000"
        />
        <div className="page-hero-overlay" />
        <div className="container page-hero-copy">
          <p className="eyebrow">Events & Functions</p>
          <h1>
            A generous Cambodian table, <em>made for your moment.</em>
          </h1>
          <p>
            Chef Neri’s family recipes, fragrant kroeung, signature Fish Amok
            and warm, attentive hospitality for weddings, engagements,
            birthdays, corporate functions and celebrations.
          </p>
          <div className="inline-actions event-hero-actions">
            <a className="button button-gold" href="#enquire">
              Check Your Date <ArrowRight size={17} />
            </a>
            <DownloadPackage placement="groups_hero" />
          </div>
        </div>
        <figure className="page-hero-inset">
          <img
            src={SITE.images.grandmothersDish}
            alt="My Grandmother’s Dish prepared for sharing at ANGKOR"
            width="900"
            height="1125"
          />
          <figcaption>Family recipes, made to share</figcaption>
        </figure>
      </section>

      <section
        className="event-sales-strip"
        aria-label="Function package highlights"
      >
        <div className="container">
          <div>
            <strong>12–24</strong>
            <span>feast guests without exclusive hire</span>
          </div>
          <div>
            <strong>38</strong>
            <span>maximum seated, exclusive hire</span>
          </div>
          <div>
            <strong>50%</strong>
            <span>deposit to secure your date</span>
          </div>
          <div>
            <strong>7 days</strong>
            <span>final details and balance</span>
          </div>
        </div>
      </section>

      <section className="section parchment-section">
        <div className="container celebration-grid">
          <div>
            <p className="eyebrow dark">Made for your occasion</p>
            <h2>
              Celebrate with food <em>at the centre.</em>
            </h2>
            <p className="lead">
              Choose a shared feast for a group table or take over the whole
              restaurant. Every event begins with a direct conversation about
              your date, guest numbers and the feeling you want to create.
            </p>
            <div className="event-location-cue">
              <MapPin aria-hidden="true" />
              <span>
                389 Spencer Street, West Melbourne · near Southern Cross and
                Marvel Stadium
              </span>
            </div>
          </div>
          <div className="occasion-list">
            {EVENT_TYPES.map(type => (
              <span key={type}>{type}</span>
            ))}
          </div>
        </div>
      </section>

      <section
        className="section banquet-section event-feasts-section"
        id="packages"
      >
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Cambodian event feasts</p>
              <h2>
                Two ways to <em>fill the table.</em>
              </h2>
            </div>
            <p>
              Minimum 12 adults. Children aged 5–11 have a dedicated price;
              children under 5 are complimentary. Sample menus may vary
              seasonally and remain subject to availability.
            </p>
          </div>

          <figure className="rev25-feast-menu" style={{ margin: 0 }}>
            <img
              src={FEAST_MENU_REV25.image}
              alt="Angkor Feast and Royal Khmer Feast menus for groups of 12 or more"
              width="1240"
              height="1754"
              loading="lazy"
              decoding="async"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
          </figure>
          <div className="inline-actions" style={{ marginTop: "1.5rem" }}>
            <a
              className="button button-gold"
              href={FEAST_MENU_REV25.pdf}
              download="angkor-feast-menus-FINAL-rev25.pdf"
            >
              <Download size={17} aria-hidden="true" /> Download the Feast Menus (PDF)
            </a>
          </div>
        </div>
      </section>

      <section className="section exclusive-hire-section">
        <div className="container exclusive-hire-grid">
          <div>
            <p className="eyebrow dark">Exclusive venue hire</p>
            <h2>
              Make the whole restaurant <em>yours.</em>
            </h2>
            <p>
              Welcome up to 38 seated guests with no room-hire fee. Simply reach
              the food and beverage minimum spend for your service. Dinner hire
              runs for four hours and lunch hire for three hours, with 60
              minutes’ set-up access beforehand.
            </p>
            <ul className="event-feature-list">
              <li>
                Groups of 12–24 can dine on either feast without exclusive hire.
              </li>
              <li>Monday private hire is available on request.</li>
              <li>Food and beverages count towards the minimum spend.</li>
            </ul>
          </div>
          <div
            className="venue-minimum-table"
            role="table"
            aria-label="Exclusive venue minimum spends"
          >
            {VENUE_MINIMUMS.map(([service, timing, minimum]) => (
              <div role="row" key={service}>
                <span role="cell">
                  <strong>{service}</strong>
                  <small>{timing}</small>
                </span>
                <b role="cell">{minimum}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="container event-pricing-note">
          A 20% surcharge applies on Sundays. Public holidays are closed except
          for exclusive hire, with a 50% surcharge on the minimum spend and
          menu.
        </div>
      </section>

      <section className="section drinks-section">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Drinks</p>
              <h2>
                Pour the welcome. <em>Set the mood.</em>
              </h2>
            </div>
            <p>
              Drinks packages apply to all adult guests aged 18+ and are served
              under Responsible Service of Alcohol. A flexible bar tab is also
              available.
            </p>
          </div>
          <div className="drink-package-grid">
            {DRINK_PACKAGES.map((drink, index) => (
              <article key={drink.name}>
                <span>0{index + 1}</span>
                <GlassWater aria-hidden="true" />
                <h3>{drink.name}</h3>
                <strong>{drink.price}</strong>
                <p>{drink.detail}</p>
              </article>
            ))}
          </div>
          <p className="event-pricing-note light">
            BYO wine is available for exclusive hire by prior arrangement at $15
            corkage per 750 ml bottle.
          </p>
        </div>
      </section>

      <section className="section event-upgrades-section">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow dark">Make it yours</p>
              <h2>
                Add the touches <em>that matter.</em>
              </h2>
            </div>
            <p>
              Build on either feast with food, welcome drinks, celebration cake
              service or thoughtful styling. Extended hours are available for
              exclusive hire, subject to licensed hours.
            </p>
          </div>
          <div className="event-upgrade-grid">
            <div>
              <h3>From the kitchen</h3>
              <dl>
                {kitchenUpgrades.map(([name, price]) => (
                  <div key={name}>
                    <dt>{name}</dt>
                    <dd>{price}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <h3>Celebrate & style</h3>
              <dl>
                {styleUpgrades.map(([name, price]) => (
                  <div key={name}>
                    <dt>{name}</dt>
                    <dd>{price}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <p className="event-pricing-note">
            Extended hours: $450 per extra weekday hour or $475 on Sunday,
            subject to licensed hours; public-holiday extended hours are double.
          </p>
        </div>
      </section>

      <section className="section event-download-section">
        <div className="container event-download-grid">
          <div>
            <Sparkles aria-hidden="true" />
            <p className="eyebrow">Everything in one place</p>
            <h2>
              Download the full <em>event package.</em>
            </h2>
            <p>
              Keep the menus, venue minimums, drinks, upgrades and complete
              booking terms together while you plan.
            </p>
          </div>
          <DownloadPackage placement="groups_download" />
        </div>
      </section>

      <section className="section event-terms-section">
        <div className="container">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow dark">Booking terms at a glance</p>
              <h2>
                Clear steps, <em>before the celebration.</em>
              </h2>
            </div>
            <p>
              The downloadable event package contains the full terms. These
              highlights help your group plan confidently from the first
              enquiry.
            </p>
          </div>
          <div className="event-terms-grid">
            <article>
              <span>01</span>
              <h3>Enquire</h3>
              <p>Share your preferred date, guest numbers and occasion.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Confirm</h3>
              <p>
                A 50% deposit of the estimated package total, or of the minimum
                spend for exclusive hire, secures your date.
              </p>
            </article>
            <article>
              <span>03</span>
              <h3>Finalise</h3>
              <p>
                Final numbers, menu, dietary needs and balance are due 7 days
                before.
              </p>
            </article>
            <article>
              <span>04</span>
              <h3>Celebrate</h3>
              <p>We prepare the table so you can enjoy the occasion.</p>
            </article>
          </div>
          <div className="event-allergy-note">
            <strong>Dietary requirements and allergies</strong>
            <p>
              Vegetarian, vegan and gluten-free alternatives are available at no
              extra cost with 7 days’ notice. Our kitchen uses peanuts, sesame,
              gluten, milk, fish, shellfish and molluscs, egg, soy and coconut;
              ANGKOR cannot guarantee an allergen-free meal.
            </p>
          </div>
        </div>
      </section>

      <section className="section enquiry-section" id="enquire">
        <div className="container enquiry-grid">
          <div>
            <p className="eyebrow dark">Plan your celebration</p>
            <h2>
              Tell us what you <em>have in mind.</em>
            </h2>
            <p>
              Share the details below. Your email app will open with the enquiry
              ready for you to review and send directly to ANGKOR.
            </p>
            <p>
              Our team will help you choose the feast, drinks and venue format that fits.
            </p>
            <div className="event-contact-stack">
              <a href={`tel:${SITE.phone}`} className="text-link">
                <Phone size={16} /> {SITE.phoneDisplay}
              </a>
              <a href={`mailto:${SITE.eventsEmail}`} className="text-link">
                <Mail size={16} /> {SITE.eventsEmail}
              </a>
              <DownloadPackage placement="groups_enquiry" />
            </div>
          </div>
          <form onSubmit={submit}>
            <label>
              Name
              <input required name="Name" autoComplete="name" />
            </label>
            <label>
              Email
              <input required type="email" name="Email" autoComplete="email" />
            </label>
            <label>
              Telephone
              <input required type="tel" name="Telephone" autoComplete="tel" />
            </label>
            <div className="field-row">
              <label>
                Preferred date
                <input
                  required
                  type="date"
                  name="Preferred date"
                  min={minimumDate}
                />
              </label>
              <label>
                Preferred time
                <input required type="time" name="Preferred time" />
              </label>
            </div>
            <label>
              Number of guests
              <input
                required
                type="number"
                min="12"
                max="38"
                name="Number of guests"
                aria-describedby="event-guest-guidance"
              />
              <small id="event-guest-guidance">
                Event feasts require at least 12 adults. For smaller tables,
                please use the regular booking link.
              </small>
            </label>
            <label>
              Occasion
              <select required name="Occasion" defaultValue="">
                <option value="" disabled>
                  Select your occasion
                </option>
                {EVENT_TYPES.map(type => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </label>
            <label>
              Preferred package
              <select name="Preferred package" defaultValue="Not sure yet">
                <option>Not sure yet</option>
                <option>Angkor Feast · $79 per adult</option>
                <option>Royal Khmer Feast · $99 per adult</option>
                <option>Exclusive venue hire</option>
              </select>
            </label>
            <label>
              Dietary requirements
              <textarea name="Dietary requirements" rows={3} />
            </label>
            <label>
              Message
              <textarea name="Message" rows={5} />
            </label>
            <button className="button button-dark" type="submit">
              <CalendarDays size={17} /> Open Enquiry Email
            </button>
            {copyMessage && (
              <button
                className="button button-outline"
                type="button"
                onClick={copyEnquiry}
              >
                Copy Enquiry Details
              </button>
            )}
            <p className="enquiry-fallback" aria-live="polite">
              {copyStatus ||
                `If your email app does not open, email ${SITE.eventsEmail} or call ${SITE.phoneDisplay}.`}
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
